/**
 * The notification center.
 *
 * In-app only, by product decision (PRD 2): no SMS, no email, no push. This page
 * is the whole delivery mechanism, so it has to be worth opening — unread first,
 * grouped by day, one tap to clear.
 *
 * The audience rules (kids never see money) are enforced in SQL by
 * worker/routes/notifications.ts. Nothing here is a security control.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useIdentity } from '../../lib/identity';
import type { Notification } from '../../shared/types';
import { metaFor, CaughtUpIcon } from './meta';
import { CommentThread } from '../notifications-extras/CommentThread';

/** Quiet background refresh. Once a minute is plenty for a household. */
const POLL_MS = 60_000;

export default function NotificationsPage() {
  const identity = useIdentity();
  const navigate = useNavigate();

  const [items, setItems] = useState<Notification[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);

  /** Ids we've optimistically marked read but haven't heard back on yet. */
  const pendingRead = useRef<Set<number>>(new Set());

  const load = useCallback(async (mode: 'initial' | 'quiet') => {
    try {
      const rows = await api.get<Notification[]>('/notifications');
      // Don't let a poll that raced an in-flight mark-read flip a row back to unread.
      setItems(
        rows.map((n) =>
          !n.read_at && pendingRead.current.has(n.id)
            ? { ...n, read_at: new Date().toISOString() }
            : n
        )
      );
      setLoadError(false);
    } catch {
      // A failed background poll shouldn't blow away a good list.
      if (mode === 'initial') setLoadError(true);
    }
  }, []);

  useEffect(() => {
    void load('initial');

    const tick = () => {
      if (document.visibilityState === 'visible') void load('quiet');
    };
    const timer = window.setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', tick);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [load]);

  const unread = useMemo(() => (items ?? []).filter((n) => !n.read_at), [items]);
  const earlier = useMemo(() => (items ?? []).filter((n) => n.read_at), [items]);

  function open(n: Notification) {
    setActionError(null);

    if (!n.read_at) {
      const now = new Date().toISOString();
      pendingRead.current.add(n.id);
      setItems((prev) => prev?.map((x) => (x.id === n.id ? { ...x, read_at: now } : x)) ?? prev);

      api
        .post<Notification>(`/notifications/${n.id}/read`, {})
        .then((saved) => {
          setItems((prev) => prev?.map((x) => (x.id === saved.id ? saved : x)) ?? prev);
        })
        .catch(() => {
          // Put it back the way it was and say so.
          setItems((prev) => prev?.map((x) => (x.id === n.id ? { ...x, read_at: null } : x)) ?? prev);
          setActionError("Couldn't mark that as read. Check your connection and try again.");
        })
        .finally(() => {
          pendingRead.current.delete(n.id);
        });
    }

    if (n.link) navigate(n.link);
  }

  async function markAllRead() {
    setActionError(null);
    setClearing(true);
    try {
      const rows = await api.post<Notification[]>('/notifications/read-all', {});
      setItems(rows);
    } catch {
      setActionError("Couldn't clear these. Check your connection and try again.");
    } finally {
      setClearing(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-ink">Notifications</h1>
          <p className="mt-1 text-sm text-ink-soft" aria-live="polite">
            {items === null
              ? 'Checking for anything new…'
              : unread.length > 0
                ? `${unread.length} unread`
                : "You're all caught up"}
          </p>
        </div>

        {unread.length > 0 && (
          <button
            type="button"
            onClick={markAllRead}
            disabled={clearing}
            className="inline-flex min-h-11 items-center rounded-full border border-border bg-surface px-4 text-sm font-medium text-ink transition-colors hover:bg-surface-sunk focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusk disabled:opacity-60"
          >
            {clearing ? 'Clearing…' : 'Mark all read'}
          </button>
        )}
      </header>

      {actionError && (
        <p
          role="alert"
          className="mt-4 rounded-card border border-clay/30 bg-clay-soft px-4 py-3 text-sm text-clay"
        >
          {actionError}
        </p>
      )}

      <div className="mt-6">
        {items === null && !loadError && <Skeleton />}

        {loadError && (
          <div className="rounded-card border border-border bg-surface p-8 text-center">
            <h2 className="font-display text-lg text-ink">We couldn't load your notifications</h2>
            <p className="mx-auto mt-2 max-w-sm text-sm text-ink-soft">
              The house is fine — the app just couldn't reach the server.
            </p>
            <button
              type="button"
              onClick={() => {
                setLoadError(false);
                setItems(null);
                void load('initial');
              }}
              className="mt-5 inline-flex min-h-11 items-center rounded-full bg-sage px-5 text-sm font-medium text-white transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusk"
            >
              Try again
            </button>
          </div>
        )}

        {items !== null && items.length === 0 && !loadError && (
          <EmptyState isAdult={identity.role === 'adult'} />
        )}

        {items !== null && items.length > 0 && (
          <div className="space-y-8">
            {unread.length > 0 && (
              <Section title="New" items={unread} onOpen={open} />
            )}
            {earlier.length > 0 && (
              <Section title={unread.length > 0 ? 'Earlier' : 'Recent'} items={earlier} onOpen={open} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- sections

function Section({
  title,
  items,
  onOpen,
}: {
  title: string;
  items: Notification[];
  onOpen: (n: Notification) => void;
}) {
  const days = useMemo(() => groupByDay(items), [items]);

  return (
    <section aria-label={title}>
      <h2 className="text-xs font-semibold tracking-wide text-ink-faint uppercase">{title}</h2>

      {days.map((day) => (
        <div key={day.key} className="mt-4">
          <h3 className="mb-2 text-xs font-medium text-ink-faint">{day.heading}</h3>
          <ul className="space-y-2">
            {day.items.map((n) => (
              <Row key={n.id} n={n} onOpen={onOpen} />
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

function Row({ n, onOpen }: { n: Notification; onOpen: (n: Notification) => void }) {
  const meta = metaFor(n.type);
  const at = parseTs(n.created_at);
  const isUnread = !n.read_at;
  // Replies live under each notification — this is the "comments in the bell". They
  // only load when opened, so a full list doesn't fire a comment fetch per row.
  const [showReplies, setShowReplies] = useState(false);

  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(n)}
        className={`flex min-h-11 w-full items-start gap-3 rounded-card border p-4 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusk ${
          isUnread
            ? 'border-border bg-surface shadow-sm hover:bg-surface-sunk'
            : 'border-transparent bg-surface/60 hover:border-border hover:bg-surface'
        }`}
      >
        <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${meta.chip}`}>
          {meta.icon}
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span
              className={
                isUnread ? 'text-sm font-semibold text-ink' : 'text-sm font-medium text-ink-soft'
              }
            >
              {n.title}
            </span>
            {isUnread && (
              <span className="rounded-full bg-dusk-soft px-1.5 py-0.5 text-[11px] font-semibold text-dusk">
                New
              </span>
            )}
          </span>

          {n.body && <span className="mt-1 block text-sm text-ink-soft">{n.body}</span>}

          <span className="mt-2 flex flex-wrap items-center gap-x-2 text-xs text-ink-faint">
            <span>{meta.label}</span>
            <span aria-hidden="true">·</span>
            <time dateTime={at.toISOString()}>{relativeTime(at)}</time>
            {n.link && (
              <>
                <span aria-hidden="true">·</span>
                <span className="font-medium text-dusk">Open</span>
              </>
            )}
          </span>
        </span>
      </button>

      <div className="mt-1 pl-14">
        <button
          type="button"
          onClick={() => setShowReplies((v) => !v)}
          className="text-xs font-medium text-dusk hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusk"
          aria-expanded={showReplies}
        >
          {showReplies ? 'Hide replies' : 'Reply'}
        </button>
        {showReplies && (
          <div className="mt-2">
            {/* It's in the caller's own list, so the API already deemed it visible. */}
            <CommentThread notificationId={n.id} canSee />
          </div>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------- states

function EmptyState({ isAdult }: { isAdult: boolean }) {
  return (
    <div className="rounded-card border border-border bg-surface p-10 text-center">
      <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-sage-soft text-sage">
        {CaughtUpIcon}
      </div>

      <h2 className="mt-5 font-display text-xl font-semibold text-ink">You're all caught up</h2>

      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-ink-soft">
        {isAdult
          ? 'Nothing needs you right now. When a category gets close to its cap, a chore is waiting on your approval, a subscription shows up or changes price, or the week has no meals planned, it lands here.'
          : 'Nothing needs you right now. When you get a new chore, a chore is approved or sent back, allowance is paid out, or the week needs a meal plan, you’ll hear about it here.'}
      </p>

      <div className="mt-6 flex flex-wrap justify-center gap-2">
        <Link
          to="/"
          className="inline-flex min-h-11 items-center rounded-full bg-sage px-5 text-sm font-medium text-white transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusk"
        >
          Back to the hub
        </Link>
        <Link
          to="/chores"
          className="inline-flex min-h-11 items-center rounded-full border border-border bg-surface px-5 text-sm font-medium text-ink transition-colors hover:bg-surface-sunk focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusk"
        >
          {isAdult ? 'Review chores' : 'See my chores'}
        </Link>
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <ul className="space-y-2" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <li key={i} className="flex animate-pulse items-start gap-3 rounded-card bg-surface p-4">
          <span className="h-10 w-10 shrink-0 rounded-full bg-surface-sunk" />
          <span className="flex-1 space-y-2 py-1">
            <span className="block h-3 w-2/5 rounded bg-surface-sunk" />
            <span className="block h-3 w-4/5 rounded bg-surface-sunk" />
          </span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------- time

/**
 * SQLite writes `datetime('now')` as 'YYYY-MM-DD HH:MM:SS' in UTC with no zone
 * marker. Safari refuses to parse that, and every other browser would read it as
 * local time and be hours off. Normalise it before trusting `new Date()`.
 */
function parseTs(value: string): Date {
  const iso = value.includes('T') ? value : value.replace(' ', 'T');
  const zoned = /([zZ]|[+-]\d{2}:?\d{2})$/.test(iso);
  return new Date(zoned ? iso : `${iso}Z`);
}

function relativeTime(at: Date): string {
  const seconds = Math.max(0, (Date.now() - at.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  return at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

interface DayGroup {
  key: string;
  heading: string;
  items: Notification[];
}

/** Groups a list (already newest-first from the API) into local calendar days. */
function groupByDay(items: Notification[]): DayGroup[] {
  const groups: DayGroup[] = [];

  for (const n of items) {
    const at = parseTs(n.created_at);
    const key = localDayKey(at);
    const last = groups[groups.length - 1];

    if (last && last.key === key) last.items.push(n);
    else groups.push({ key, heading: dayHeading(at), items: [n] });
  }

  return groups;
}

function localDayKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

function dayHeading(d: Date): string {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (localDayKey(d) === localDayKey(today)) return 'Today';
  if (localDayKey(d) === localDayKey(yesterday)) return 'Yesterday';

  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}
